/*!
 * vue-grid v0.10.22 
 * (c) 2023 Alex Tkachenko
 * Released under the MIT License.
 */
import { generator, prepareProps, prepareResolver } from '@aspectus/vue-bem-styled-tag';
import { mapSame, StylingPropsMixin } from '@aspectus/vue-bem-styling';
import { Tag } from '@aspectus/vue-tag';
import { createBlockGenerator } from '@aspectus/bem';
import { makeF, mergeContext } from '@aspectus/vue-utils';

var BEM_CONFIG = {
  n: 'g-'
};
var b = createBlockGenerator(BEM_CONFIG);

var block = b('row');
var Row = Object.assign(generator({
  tag: Tag,
  block: block,
  modifiers: mapSame('justify', 'content', 'space')
}), {
  name: 'row'
});

function _typeof(obj) {
  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function (obj) {
      return typeof obj;
    };
  } else {
    _typeof = function (obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

function _isPlaceholder(a) {
  return a != null && _typeof(a) === 'object' && a['@@functional/placeholder'] === true;
}

var _isPlaceholder_1 = _isPlaceholder;

/**
 * Optimized internal one-arity curry function.
 *
 * @private
 * @category Function
 * @param {Function} fn The function to curry.
 * @return {Function} The curried function.
 */

function _curry1(fn) {
  return function f1(a) {
    if (arguments.length === 0 || _isPlaceholder_1(a)) {
      return f1;
    } else {
      return fn.apply(this, arguments);
    }
  };
}

var _curry1_1 = _curry1;

/**
 * Tests whether or not an object is an array.
 *
 * @private
 * @param {*} val The object to test.
 * @return {Boolean} `true` if `val` is an array, `false` otherwise.
 * @example
 *
 *      _isArray([]); //=> true
 *      _isArray(null); //=> false
 *      _isArray({}); //=> false
 */
var _isArray = Array.isArray || function _isArray(val) {
  return val != null && val.length >= 0 && Object.prototype.toString.call(val) === '[object Array]';
};

function _isString(x) {
  return Object.prototype.toString.call(x) === '[object String]';
}

var _isString_1 = _isString;

/**
 * Tests whether or not an object is similar to an array.
 *
 * @private
 * @category Type
 * @category List
 * @sig * -> Boolean
 * @param {*} x The object to test.
 * @return {Boolean} `true` if `x` has a numeric length property and extreme indices defined; `false` otherwise.
 * @example
 *
 *      _isArrayLike([]); //=> true
 *      _isArrayLike(true); //=> false
 *      _isArrayLike({}); //=> false
 *      _isArrayLike({length: 10}); //=> false
 *      _isArrayLike({0: 'zero', 9: 'nine', length: 10}); //=> true
 */

var _isArrayLike =
/*#__PURE__*/
_curry1_1(function isArrayLike(x) {
  if (_isArray(x)) {
    return true;
  }

  if (!x) {
    return false;
  }

  if (_typeof(x) !== 'object') {
    return false;
  }

  if (_isString_1(x)) {
    return false;
  }

  if (x.nodeType === 1) {
    return !!x.length;
  }

  if (x.length === 0) {
    return true;
  }

  if (x.length > 0) {
    return x.hasOwnProperty(0) && x.hasOwnProperty(x.length - 1);
  }

  return false;
});

var _isArrayLike_1 = _isArrayLike;

/**
 * `_makeFlat` is a helper function that returns a one-level or fully recursive
 * function based on the flag passed in.
 *
 * @private
 */

function _makeFlat(recursive) {
  return function flatt(list) {
    var value, jlen, j;
    var result = [];
    var idx = 0;
    var ilen = list.length;

    while (idx < ilen) {
      if (_isArrayLike_1(list[idx])) {
        value = recursive ? flatt(list[idx]) : list[idx];
        j = 0;
        jlen = value.length;

        while (j < jlen) {
          result[result.length] = value[j];
          j += 1;
        }
      } else {
        result[result.length] = list[idx];
      }

      idx += 1;
    }

    return result;
  };
}

var _makeFlat_1 = _makeFlat;

/**
 * Returns a new list by pulling every item out of it (and all its sub-arrays)
 * and putting them in a new array, depth-first.
 *
 * @func
 * @memberOf R
 * @since v0.1.0
 * @category List
 * @sig [a] -> [b]
 * @param {Array} list The array to consider.
 * @return {Array} The flattened list.
 * @see R.unnest
 * @example
 *
 *      R.flatten([1, 2, [3, 4], 5, [6, [7, 8, [9, [10, 11], 12]]]]);
 *      //=> [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
 */

var flatten =
/*#__PURE__*/
_curry1_1(
/*#__PURE__*/
_makeFlat_1(true));

var flatten_1 = flatten;

var block$1 = b('cols');
var normalizerRegex = /\s/img;
function normalizePassedTypes(elements) {
  return flatten_1(flatten_1(elements).filter(function (x) {
    return !!x;
  }).map(function (x) {
    return x.split(normalizerRegex);
  }));
}
function cols() {
  for (var _len = arguments.length, types = new Array(_len), _key = 0; _key < _len; _key++) {
    types[_key] = arguments[_key];
  }

  return block$1(normalizePassedTypes(types).reduce(function (acc, type) {
    acc[type] = true;
    return acc;
  }, {}));
}

var block$2 = b('cell');
var options = {
  modifiers: {
    justify: 'justify'
  }
};
var resolver = prepareResolver(options);
var Cell = makeF(function (h, context) {
  var c = context.props.cols;

  var _resolver = resolver(context.props),
      _resolver2 = _slicedToArray(_resolver, 1),
      modifiers = _resolver2[0];

  return h(Tag, mergeContext(context.data, {
    class: [block$2(modifiers), c && cols(c) || null]
  }), context.children);
}, Object.assign(prepareProps(options), {
  cols: {}
}), 'cell', {
  mixins: [StylingPropsMixin]
});

var block$3 = b('container');
var Container = Object.assign(generator({
  tag: Tag,
  block: block$3,
  modifiers: {
    space: 'space'
  }
}), {
  name: 'container'
});

function install(Vue) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$containerName = _ref.containerName,
      containerName = _ref$containerName === void 0 ? Container.name : _ref$containerName,
      _ref$rowName = _ref.rowName,
      rowName = _ref$rowName === void 0 ? Row.name : _ref$rowName,
      _ref$cellName = _ref.cellName,
      cellName = _ref$cellName === void 0 ? Cell.name : _ref$cellName;

  Vue.component(containerName, Container);
  Vue.component(rowName, Row);
  Vue.component(cellName, Cell);
}
var main = {
  Cell: Cell,
  Row: Row,
  Container: Container,
  cols: cols,
  b: b,
  install: install
};

export default main;
export { Cell, Container, Row, b, cols, install };
